import { Editor, Node, Path } from 'slate';

import { resolveParagraphEntryByLocationAnchor } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { serializeRemovedBlocksToPreviewHtml } from '../../../serializers/html';
import {
  AlineTransforms,
  EditingMode,
  isParagraphElement,
  markParagraphAsSuggestedDelete,
  Queries,
} from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyRemoveOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * The locationId of the last paragraph of the range being removed
   */
  endLocationAnchor: string;
  /**
   * Identifies the whole removal as one group, so accept/reject acts on it
   * as a unit
   */
  groupId: string;
  /**
   * Why the content is being removed — stored on the first sourced edit of
   * the removed range
   */
  reasoning: string;
  /**
   * The source stamp carried by every marked edit — or undefined to
   * materialize the removal as the author's own tracked changes, with no
   * source stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
  /**
   * The locationId of the first paragraph of the range being removed
   */
  startLocationAnchor: string;
}

/**
 * Applies a `remove` structured edit output to the editor as a suggested
 * deletion of every block from the start paragraph through the end paragraph
 * (inclusive). Unlike a user's selection delete, this is block removal:
 * accepting leaves no empty-paragraph residue.
 *
 * The flow:
 * 1. Resolves both anchor paragraphs by locationId. They must be siblings
 *    (same parent); the range between them may contain other paragraphs and
 *    whole tables. Reversed anchors are tolerated and swapped.
 * 2. Suggests deletion of the whole range via `suggestDeletion`, which marks
 *    text with `deleteNode`, crossed pilcrows with `deleteParagraph`, and
 *    fully-covered table rows with `removeTableRow` — all carrying the
 *    caller's source.
 * 3. Stamps `deleteParagraph` on every top-level paragraph in the range that
 *    `suggestDeletion` skipped (the end paragraph, and paragraphs followed by
 *    a table). On accept, `mergeWithNextParagraph` merges each into its next
 *    sibling paragraph, or removes it once emptied when no paragraph follows
 *    — this is what upgrades "delete the selected characters" to "remove
 *    these blocks".
 * 4. Stores the reasoning on the first sourced edit of the range. Block
 *    suggestions carry no suggestion-marker pair — the sourced edits
 *    themselves are the suggestion, resolved as one unit by groupId.
 *
 * Returns the removed range's preview HTML (captured before marking) for the
 * chat card — tables keep their structure and list items keep their marker
 * attributes.
 */
export function applyRemoveOutput(
  editor: Editor,
  options: ApplyRemoveOutputOptions
): { removedHtml: string } {
  const { authorId, endLocationAnchor, groupId, reasoning, source, startLocationAnchor } = options;

  const startPath = resolveParagraphPath(editor, startLocationAnchor);
  const endPath =
    endLocationAnchor === startLocationAnchor
      ? startPath
      : resolveParagraphPath(editor, endLocationAnchor);

  if (!Path.equals(Path.parent(startPath), Path.parent(endPath))) {
    throw new Error(
      `Remove anchors '${startLocationAnchor}' and '${endLocationAnchor}' are not siblings — a removal range cannot cross into or out of a table cell`
    );
  }

  // Tolerate reversed anchors: both exist, so the intended range is unambiguous.
  const [firstPath, lastPath] =
    Path.compare(startPath, endPath) <= 0 ? [startPath, endPath] : [endPath, startPath];

  const range = Editor.range(editor, Editor.start(editor, firstPath), Editor.end(editor, lastPath));

  // Captured before marking so the previews show the original content.
  const rangeParentPath = Path.parent(firstPath);
  const removedBlocks: Node[] = [];
  for (
    let index = Queries.indexInParent(firstPath);
    index <= Queries.indexInParent(lastPath);
    index++
  ) {
    removedBlocks.push(Node.get(editor, [...rangeParentPath, index]));
  }
  const removedHtml = serializeRemovedBlocksToPreviewHtml(removedBlocks, {
    listDefinitions: Queries.getNumbering(editor).listDefinitions,
  });

  /**
   * `suggestDeletion` can remove nodes outright (it cancels the author's own
   * earlier suggested insertions inside the range), shifting paths. Track the
   * range's blocks through it with pathRefs.
   */
  const firstPathRef = Editor.pathRef(editor, firstPath);
  const lastPathRef = Editor.pathRef(editor, lastPath);

  AlineTransforms.suggestDeletion(editor, {
    at: range,
    authorId,
    editingMode: EditingMode.SUGGESTING,
    select: false,
    source,
  });

  const firstPathAfter = firstPathRef.unref();
  const lastPathAfter = lastPathRef.unref();
  if (!firstPathAfter || !lastPathAfter) {
    throw new Error(
      `Remove range '${startLocationAnchor}'–'${endLocationAnchor}' had no content left to remove (it consisted of this suggestion author's own pending insertions)`
    );
  }

  /**
   * Stamp the pilcrows `suggestDeletion` deliberately leaves alone. For block
   * removal every paragraph in the range must merge away on accept, including
   * the last one and any paragraph whose next sibling is a table.
   * `markParagraphAsSuggestedDelete` no-ops on the ones already stamped.
   */
  const parentPath = Path.parent(firstPathAfter);
  const firstIndex = Queries.indexInParent(firstPathAfter);
  const lastIndex = Queries.indexInParent(lastPathAfter);
  for (let index = firstIndex; index <= lastIndex; index++) {
    const blockPath = [...parentPath, index];
    if (!isParagraphElement(Node.get(editor, blockPath))) continue;
    markParagraphAsSuggestedDelete(editor, blockPath, { authorId, source });
  }

  // A sourceless removal has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (source) {
    stampReasoningOnFirstSourcedEdit(editor, {
      at: Editor.range(editor, firstPathAfter, lastPathAfter),
      groupId,
      reasoning,
    });
  }

  return { removedHtml };
}

/**
 * A maximal run of removable sibling paragraphs, expressed as the
 * locationAnchors of its first and last paragraph — the input shape
 * `applyRemoveOutput` takes.
 */
export interface RemoveAnchorRun {
  endLocationAnchor: string;
  startLocationAnchor: string;
}

/**
 * Partitions a set of paragraph locationAnchors into contiguous sibling runs,
 * each removable as one suggestion via `applyRemoveOutput`.
 *
 * Two consecutive listed paragraphs (in document order) belong to the same
 * run when they share a parent and every block strictly between them is a
 * non-paragraph (e.g. a table) — so a table between two listed paragraphs is
 * removed with them, while an unlisted paragraph between them splits the
 * removal into two independent suggestions. Anchors are deduplicated; an
 * unknown anchor throws.
 */
export function partitionRemoveAnchorsIntoRuns(
  editor: Editor,
  locationAnchors: string[]
): RemoveAnchorRun[] {
  const entries = [...new Set(locationAnchors)]
    .map(anchor => ({ anchor, path: resolveParagraphPath(editor, anchor) }))
    .sort((a, b) => Path.compare(a.path, b.path));

  const runs: RemoveAnchorRun[] = [];
  let run: { end: (typeof entries)[number]; start: (typeof entries)[number] } | null = null;

  for (const entry of entries) {
    if (run && getIsSameRun(editor, run.end.path, entry.path)) {
      run.end = entry;
      continue;
    }
    if (run) {
      runs.push({ endLocationAnchor: run.end.anchor, startLocationAnchor: run.start.anchor });
    }
    run = { end: entry, start: entry };
  }
  if (run) {
    runs.push({ endLocationAnchor: run.end.anchor, startLocationAnchor: run.start.anchor });
  }

  return runs;
}

function getIsSameRun(editor: Editor, previousPath: Path, nextPath: Path): boolean {
  const parentPath = Path.parent(previousPath);
  if (!Path.equals(parentPath, Path.parent(nextPath))) return false;

  const previousIndex = Queries.indexInParent(previousPath);
  const nextIndex = Queries.indexInParent(nextPath);
  for (let index = previousIndex + 1; index < nextIndex; index++) {
    if (isParagraphElement(Node.get(editor, [...parentPath, index]))) return false;
  }
  return true;
}

function resolveParagraphPath(editor: Editor, locationAnchor: string): Path {
  const [, path] = resolveParagraphEntryByLocationAnchor(editor, locationAnchor);
  return path;
}
